import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useBusinesses } from "../../../services/businessService";

const Businesses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDark = useSelector((state: RootState) => state.theme.darkMode);
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  // Fetch businesses from API
  const {
    data: businessesResponse,
    isLoading,
    error,
  } = useBusinesses({
    locale: currentLanguage,
    verified: true,
  });

  const businesses = businessesResponse?.businesses || [];

  // Get unique categories (filter out null/undefined)
  const categories = [
    ...new Set(
      businesses
        .map((business) => business.category)
        .filter((cat): cat is string => !!cat)
    ),
  ];

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
        }`}
    >
      <Header />

      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16 bg-gradient-to-br from-emerald-600 via-stone-600 to-emerald-700 text-white"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Local Businesses
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8">
              Discover and support Djichami's thriving business community
            </p>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              className="inline-block"
            >
              <div
                className={`px-6 py-3 rounded-none font-light tracking-wide ${isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-white/20 text-white"
                  }`}
              >
                <span className="text-lg font-semibold">
                  {businesses.length} Verified Businesses
                </span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Category Filter */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-8 px-4 sm:px-16"
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Browse by Category
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${isDark
                      ? "bg-gray-800 text-gray-300 hover:bg-emerald-600 hover:text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-emerald-600 hover:text-white"
                    }`}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Businesses Grid */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">
                    Loading businesses...
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-600 dark:text-red-400">
                  Error loading businesses. Please try again later.
                </p>
              </div>
            ) : businesses.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-600 dark:text-gray-400">
                  No businesses found.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {businesses.map((business, index) => (
                  <motion.div
                    key={business.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer ${isDark ? "bg-gray-800" : "bg-white"
                      }`}
                    onClick={() =>
                      navigate(`/${currentLanguage}/businesses/${business.id}`)
                    }
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={
                          business.image ||
                          "https://via.placeholder.com/400x300?text=No+Image"
                        }
                        alt={business.name || "Business"}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {business.category && (
                        <div className="absolute top-4 left-4">
                          <span
                            className={`px-3 py-1 text-xs font-medium tracking-wide rounded-full ${isDark
                                ? "bg-gray-700 text-gray-300"
                                : "bg-white/90 text-gray-900"
                              }`}
                          >
                            {business.category}
                          </span>
                        </div>
                      )}
                      {business.verified && (
                        <div className="absolute top-4 right-4">
                          <div className="flex items-center space-x-1 text-green-400 bg-black/50 px-2 py-1 rounded-full">
                            <span className="text-sm">✓</span>
                            <span className="text-xs font-medium">
                              {t("business.verified")}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-xl font-bold text-white mb-2 leading-tight">
                          {business.name || "Unnamed Business"}
                        </h3>
                        {business.description && (
                          <p className="text-white/90 text-sm line-clamp-2">
                            {business.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      {/* Rating */}
                      <div className="flex items-center space-x-2 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className={`text-lg ${i < Math.floor(business.rating || 0)
                                ? "text-yellow-400"
                                : "text-gray-300"
                              }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {business.rating?.toFixed(1) || "0.0"} (
                          {business.reviews || 0} {t("business.reviews")})
                        </span>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            📍
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 truncate">
                            {business.location || "N/A"}
                          </span>
                        </div>
                        {business.contact?.phone && (
                          <div className="flex items-center space-x-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              📞
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {business.contact.phone}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Services Preview */}
                      {business.services && business.services.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
                            Services:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {business.services
                              .slice(0, 3)
                              .map((service, idx) => (
                                <span
                                  key={idx}
                                  className={`px-2 py-1 text-xs rounded-full ${isDark
                                      ? "bg-gray-700 text-gray-300"
                                      : "bg-gray-100 text-gray-700"
                                    }`}
                                >
                                  {service}
                                </span>
                              ))}
                            {business.services.length > 3 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${isDark
                                    ? "bg-gray-700 text-gray-300"
                                    : "bg-gray-100 text-gray-700"
                                  }`}
                              >
                                +{business.services.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Button */}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        View Details
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16 bg-gradient-to-r from-emerald-600 to-stone-600 text-white"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Support Local Businesses
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Your support helps build a stronger, more vibrant Djichami
              community. Discover businesses that make our town special.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:bg-gray-50 transition-colors"
            >
              Become a Partner
            </motion.button>
          </div>
        </motion.section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Businesses;
